import { ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  SxProps,
  Typography,
} from '@mui/material';
import LelyButton from '../LelyButton/LelyButton';
import LelyLoadingButton from '../LelyLoadingButton/LelyLoadingButton';
import { LelyDialogSize } from '@/common/styles/newStyles/componentOverrides/dialog';

export type LelyDialogProps = {
  open: boolean;
  title: string;
  content: ReactNode;
  startActionButton?: string;
  startActionButtonDisabled?: boolean;
  leftActionButton?: string;
  leftActionButtonDisabled?: boolean;
  rightActionButton?: string;
  rightActionButtonDisabled?: boolean;
  isLoading?: boolean;
  hideDialogActions?: boolean;
  size?: LelyDialogSize;
  sx?: SxProps;
  id?: string;
  onClose: () => void;
  startActionHandler?: () => void;
  leftActionHandler?: () => void;
  rightActionHandler?: () => void;
};

function LelyDialog({
  open,
  title,
  content,
  startActionButton,
  startActionButtonDisabled,
  leftActionButton,
  leftActionButtonDisabled,
  rightActionButton,
  rightActionButtonDisabled,
  sx,
  hideDialogActions,
  size = 'md',
  id,
  isLoading,
  onClose,
  startActionHandler,
  leftActionHandler,
  rightActionHandler,
}: LelyDialogProps) {
  const handleClose = () => {
    if (!isLoading) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={isLoading}
      id={id}
      size={size}
      data-testid="generic-dialog"
      aria-labelledby="generic-dialog-title"
      aria-describedby="generic-dialog-description"
      slotProps={{ paper: { sx } }}
    >
      <DialogTitle
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.lelySpacing.MD,
        })}
      >
        <Typography
          variant="xl"
          component="span"
          data-testid="generic-dialog-title"
          sx={(theme) => ({
            flex: '1 0 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontFamily: theme.lelyFontFamily.NOTO,
            fontWeight: theme.typography.fontWeightMedium,
            lineHeight: theme.lelyLineHeight._2XL,
          })}
        >
          {title}
        </Typography>
        <LelyButton
          variant="tertiary"
          square
          icon="lely-icon-close"
          disabled={isLoading}
          aria-label="close"
          data-testid="dialog-close-button"
          onClick={handleClose}
          sx={{ padding: 0, flexShrink: 0 }}
        />
      </DialogTitle>

      <DialogContent data-testid="generic-dialog-content">
        {content}
      </DialogContent>

      {!hideDialogActions && (
        <DialogActions>
          {startActionButton && startActionHandler && (
            <LelyButton
              variant="secondary"
              data-testid="dialog-start-button"
              disabled={startActionButtonDisabled || isLoading}
              onClick={(e) => {
                e.stopPropagation();
                startActionHandler();
              }}
              sx={{ marginRight: 'auto' }}
            >
              {startActionButton}
            </LelyButton>
          )}

          {leftActionButton && leftActionHandler && (
            <LelyButton
              variant="tertiary"
              data-testid="dialog-cancel-button"
              disabled={leftActionButtonDisabled}
              onClick={(e) => {
                e.stopPropagation();
                leftActionHandler();
              }}
            >
              {leftActionButton}
            </LelyButton>
          )}

          {rightActionButton && rightActionHandler && (
            <LelyLoadingButton
              variant="primary"
              autoFocus
              data-testid="dialog-confirm-button"
              disabled={rightActionButtonDisabled}
              loading={isLoading}
              onClick={(e) => {
                e.stopPropagation();
                rightActionHandler();
              }}
            >
              {rightActionButton}
            </LelyLoadingButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default LelyDialog;
